import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getMonitorCourses } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { LogOut, GraduationCap, CalendarDays, Search, History } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SchedulePage from './SchedulePage';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_rip-accounting-hub/artifacts/pa6zppg0_image%2036.png";
const LOCATIONS = { san_antolin: 'San Antolin', san_martin: 'San Martin', ria_niembro: 'Ria de Niembro' };
const fmtDate = (d) => { if (!d) return ''; const p = d.split('-'); return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : d; };

export default function MonitorPortalPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [myCourses, setMyCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('cursos');
  const [search, setSearch] = useState('');
  const [calendarDate, setCalendarDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [histSearch, setHistSearch] = useState('');
  const [histCalDate, setHistCalDate] = useState(null);
  const [histCalOpen, setHistCalOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getMonitorCourses();
      setMyCourses(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const getLoc = (v) => LOCATIONS[v] || v;
  const monitorName = user?.name || '';

  // Split: upcoming vs past
  const today = new Date().toISOString().split('T')[0];
  const upcoming = myCourses.filter(c => c.date >= today);
  const past = myCourses.filter(c => c.date < today);

  // Filter upcoming (Mis Cursos)
  const filteredUpcoming = upcoming.filter(c => {
    if (calendarDate) { const d = calendarDate.toISOString().split('T')[0]; if (c.date !== d) return false; }
    if (!search) return true;
    const s = search.toLowerCase();
    return [c.date, c.time, c.location, getLoc(c.location), ...(c.reservations || []).map(r => r.reservation_name)].some(f => f && f.toLowerCase().includes(s));
  });
  const groupedUp = filteredUpcoming.reduce((acc, c) => { (acc[c.date] = acc[c.date] || []).push(c); return acc; }, {});
  const datesUp = Object.keys(groupedUp).sort((a, b) => b.localeCompare(a));

  // Filter past (Cursos impartidos)
  const filteredPast = past.filter(c => {
    if (histCalDate) { const d = histCalDate.toISOString().split('T')[0]; if (c.date !== d) return false; }
    if (!histSearch) return true;
    const s = histSearch.toLowerCase();
    return [c.date, c.time, c.location, getLoc(c.location), ...(c.reservations || []).map(r => r.reservation_name), ...(c.reservations || []).flatMap(r => (r.students || []).map(st => st.name))].some(f => f && f.toLowerCase().includes(s));
  });
  const groupedPast = filteredPast.reduce((acc, c) => { (acc[c.date] = acc[c.date] || []).push(c); return acc; }, {});
  const datesPast = Object.keys(groupedPast).sort((a, b) => b.localeCompare(a));

  const tabBtn = (id, label, icon) => (
    <button onClick={() => setTab(id)}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === id ? 'border-[#4A6D7C] text-[#4A6D7C]' : 'border-transparent text-[#8A9BA8] hover:text-[#4A6D7C]'}`}
      data-testid={`monitor-tab-${id}`}>
      {icon} {label}
    </button>
  );

  const renderCourseList = (grouped, dates, emptyMsg) => (
    dates.length === 0 ? (
      <div className="text-center py-16 text-[#8A9BA8]"><GraduationCap className="w-10 h-10 mx-auto mb-3 opacity-40"/>{emptyMsg}</div>
    ) : dates.map(date => (
      <div key={date} className="space-y-2">
        <h4 className="text-sm font-bold text-[#4A6D7C] tabular-nums">{fmtDate(date)}</h4>
        {grouped[date].map(c => {
          const resNames = (c.reservations || []).map(r => r.reservation_name).filter(Boolean).join(', ');
          const studentCount = (c.reservations || []).reduce((s, r) => s + (r.students?.length || r.num_students || 0), 0);
          const monCost = (c.monitors || []).find(m => m.name?.toLowerCase() === monitorName.toLowerCase())?.cost || 0;
          return (
            <div key={c.id} className="bg-white border border-[#E5E0D8] rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="text-lg font-bold text-[#1A2B3C] tabular-nums">{c.time}</span>
                  <span className="text-sm text-[#8A9BA8]">{getLoc(c.location)}</span>
                  {studentCount > 0 && <span className="text-xs bg-[#4A6D7C]/10 text-[#4A6D7C] px-2 py-0.5 rounded-full">{studentCount} alumnos</span>}
                </div>
                {monCost > 0 && <span className="text-sm font-semibold text-[#81B29A] tabular-nums">{monCost.toFixed(2)} €</span>}
              </div>
              {resNames && <div className="text-xs text-[#8A9BA8] mt-1">Reservas: {resNames}</div>}
            </div>
          );
        })}
      </div>
    ))
  );

  const searchBar = (searchVal, setSearchVal, calDate, setCalDate, calOpen, setCalOpen) => (
    <div className="flex items-center gap-2 mb-4">
      <Popover open={calOpen} onOpenChange={setCalOpen}>
        <PopoverTrigger asChild><Button variant="outline" size="icon" className={`border-[#E5E0D8] h-9 w-9 ${calDate ? 'bg-[#4A6D7C] text-white hover:bg-[#3A5663]' : 'text-[#4A5D6C]'}`}><CalendarDays className="w-4 h-4"/></Button></PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={calDate} onSelect={(d) => { setCalDate(d); setCalOpen(false); }} /></PopoverContent>
      </Popover>
      {calDate && <Button variant="ghost" size="sm" className="h-7 text-xs text-[#8A9BA8]" onClick={() => setCalDate(null)}>✕ {calDate.toLocaleDateString('es-ES')}</Button>}
      <div className="relative flex-1"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-[#8A9BA8]"/><Input placeholder="Buscar..." value={searchVal} onChange={e => setSearchVal(e.target.value)} className="pl-9 border-[#E5E0D8] h-9"/></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FCFBF9]" data-testid="monitor-portal">
      <div className="bg-white border-b border-[#E5E0D8] px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="SURF SKULL" className="h-8 w-auto rounded-lg" />
            <h2 className="text-lg font-bold text-[#1A2B3C]" style={{ fontFamily: 'Manrope' }}>SURF ⚡ SKULL</h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#8A9BA8]">{user?.name || user?.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="monitor-logout-btn" className="text-[#E07A5F]"><LogOut className="w-4 h-4"/></Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pt-4">
        <div className="flex gap-2 border-b border-[#E5E0D8] mb-4">
          {tabBtn('cursos', 'Mis Cursos', <GraduationCap className="w-4 h-4 inline mr-1.5" />)}
          {tabBtn('programacion', 'Programacion', <CalendarDays className="w-4 h-4 inline mr-1.5" />)}
          {tabBtn('historial', 'Cursos impartidos', <History className="w-4 h-4 inline mr-1.5" />)}
        </div>
      </div>

      {tab === 'cursos' ? (
        <div className="max-w-5xl mx-auto px-6 pb-6 space-y-4">
          {searchBar(search, setSearch, calendarDate, setCalendarDate, calendarOpen, setCalendarOpen)}
          {loading ? <div className="skeleton-pulse h-40 rounded-lg"/> : renderCourseList(groupedUp, datesUp, 'No tienes cursos asignados')}
        </div>
      ) : tab === 'historial' ? (
        <div className="max-w-5xl mx-auto px-6 pb-6 space-y-4">
          {searchBar(histSearch, setHistSearch, histCalDate, setHistCalDate, histCalOpen, setHistCalOpen)}
          {loading ? <div className="skeleton-pulse h-40 rounded-lg"/> : renderCourseList(groupedPast, datesPast, 'No hay cursos impartidos')}
        </div>
      ) : (
        <SchedulePage monitorFilter={monitorName} />
      )}
    </div>
  );
}
